/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	Box,
	Button,
	Checkbox,
	Flex,
	FormControl,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Text,
	useToast,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import c from '../../../constant';
import useStore from '../../../store';
import FileFormField from '../../../components/form/FileFormField';

const Q_CHECK = gql`
	query get($companyId: String!) {
		checkCompanyDisablementConstraint(companyId: $companyId) {
			company {
				id
				name
				nameKhmer
				logo
				active
				type
			}
			directAgents {
				id
			}
			saleProfessionals {
				id
			}
			agentPartners {
				id
				model
				companyType
				company {
					id
					name
					nameKhmer
					type
					logo
				}
				intermediary {
					id
					name
					nameKhmer
					type
					logo
				}
				createdAt
				active
				approvalId
				active
				inactiveAt
				inactiveById
				attachment
			}
			brokerPartners {
				id
				model
				company {
					id
					name
					nameKhmer
					type
					logo
				}
				intermediary {
					id
					name
					nameKhmer
					type
					logo
				}
				createdAt
				active
				approvalId
				active
				inactiveAt
				inactiveById
				attachment
			}
		}
	}
`;

const M_STATUS = gql`
	mutation update($companyId: String!, $status: Boolean!) {
		updateCompanyStatusWithoutConstraintByCompanyId(companyId: $companyId, status: $status)
	}
`;

const M_STATUS_CONSTRAINT = gql`
	mutation update(
		$companyId: String!
		$directAgentReason: String
		$directAgentInternalReason: String
		$saleStaffReason: String
		$saleStaffInternalReason: String
		$agentPartnerReason: String
		$agentPartnerInternalReason: String
		$agentPartnerDocument: Upload
		$agentPartnerDocumentSize: Float
		$brokerPartnerReason: String
		$brokerPartnerInternalReason: String
		$brokerPartnerDocument: Upload
		$brokerPartnerDocumentSize: Float
	) {
		disableCompanyWithConstraintByCompanyId(
			companyId: $companyId
			directAgentReason: $directAgentReason
			directAgentInternalReason: $directAgentInternalReason
			saleStaffReason: $saleStaffReason
			saleStaffInternalReason: $saleStaffInternalReason
			agentPartnerReason: $agentPartnerReason
			agentPartnerInternalReason: $agentPartnerInternalReason
			agentPartnerDocument: $agentPartnerDocument
			agentPartnerDocumentSize: $agentPartnerDocumentSize
			brokerPartnerReason: $brokerPartnerReason
			brokerPartnerInternalReason: $brokerPartnerInternalReason
			brokerPartnerDocument: $brokerPartnerDocument
			brokerPartnerDocumentSize: $brokerPartnerDocumentSize
		)
	}
`;

const validationSchema = Yup.object().shape({
	company: Yup.object(),
	directAgents: Yup.array(),
	directAgentCount: Yup.number(),
	saleProfessionals: Yup.array(),
	saleProfessionalCount: Yup.number(),
	agentPartners: Yup.array(),
	agentPartnerCount: Yup.number(),
	brokerPartners: Yup.array(),
	brokerPartnerCount: Yup.number(),
	directAgentReason: Yup.mixed().when('directAgentCount', {
		is: (directAgentCount) => directAgentCount > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	directAgentInternalReason: Yup.mixed().when('directAgentCount', {
		is: (directAgentCount) => directAgentCount > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	saleStaffReason: Yup.mixed().when('saleProfessionalCount', {
		is: (saleProfessionalCount) => saleProfessionalCount > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	saleStaffInternalReason: Yup.mixed().when('saleProfessionalCount', {
		is: (saleProfessionalCount) => saleProfessionalCount > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	agentPartnerReason: Yup.mixed().when('agentPartnerCount', {
		is: (agentPartnerCount) => agentPartnerCount > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	agentPartnerInternalReason: Yup.mixed().when('agentPartnerCount', {
		is: (agentPartnerCount) => agentPartnerCount > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	brokerPartnerReason: Yup.mixed().when('brokerPartnerCount', {
		is: (brokerPartnerCount) => brokerPartnerCount > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	brokerPartnerInternalReason: Yup.mixed().when('brokerPartnerCount', {
		is: (brokerPartnerCount) => brokerPartnerCount > 0,
		then: Yup.string().required('required'),
		otherwise: Yup.string().nullable(),
	}),
	agreement: Yup.mixed().when(['directAgentCount', 'saleProfessionalCount', 'agentPartnerCount', 'brokerPartnerCount'], {
		is: (directAgentCount, saleProfessionalCount, agentPartnerCount, brokerPartnerCount) =>
			directAgentCount > 0 || saleProfessionalCount > 0 || agentPartnerCount > 0 || brokerPartnerCount > 0,
		then: Yup.boolean().test('agreement', 'you must agree first', (value) => value === true),
		otherwise: Yup.boolean(),
	}),
	agentPartnerDocument: Yup.mixed().test('fileSize', 'over limit', (value) => {
		if (value) {
			return value?.size <= c.maxFileSize / 20;
		} else {
			return true;
		}
	}),
	brokerPartnerDocument: Yup.mixed().test('fileSize', 'over limit', (value) => {
		if (value) {
			return value?.size <= c.maxFileSize / 20;
		} else {
			return true;
		}
	}),
});

export default function CompanyDisablementModal({ company = null, onClose = () => null, onDataChange = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const [constraintData, setConstraintData] = useState(null);
	const [loadCheck, { data }] = useLazyQuery(Q_CHECK);
	const [updateCompanyStatus, { loading: loadingStatus }] = useMutation(M_STATUS);
	const [disableCompanyStatus, { loading: loadingStatusContraint }] = useMutation(M_STATUS_CONSTRAINT);

	const load = () => {
		if (company?.id) {
			loadCheck({ variables: { companyId: company?.id } });
		}
	};

	useEffect(load, [company?.id]);

	useEffect(() => {
		if (data?.checkCompanyDisablementConstraint) {
			setConstraintData(data?.checkCompanyDisablementConstraint);
		}
	}, [data]);

	const InputField = ({ name = null, placeholder = null, size = 'sm' | 'md' | 'lg' }) => {
		return (
			<Field name={name}>
				{({ field, meta }) => (
					<FormControl id={name}>
						<Input
							{...field}
							value={field?.value ?? ''}
							id={name}
							type="text"
							placeholder={placeholder ? placeholder : ''}
							size={size}
							errorBorderColor="red.300"
							isInvalid={meta.touched && meta.error}
							onWheelCapture={(e) => e.target.blur()}
						/>
					</FormControl>
				)}
			</Field>
		);
	};

	function isAgentCompany(type) {
		return type === c.insuranceCompanyType.agent.value;
	}

	function isBrokerCompany(type) {
		return type === c.insuranceCompanyType.broker.value;
	}

	function isInsuranceCompany(type) {
		return type === c.insuranceCompanyType.general.value || type === c.insuranceCompanyType.life.value || type === c.insuranceCompanyType.micro.value;
	}

	return (
		<>
			{constraintData && (
				<>
					{constraintData?.directAgents?.length > 0 ||
					constraintData?.saleProfessionals?.length > 0 ||
					constraintData?.agentPartners?.length > 0 ||
					constraintData?.brokerPartners?.length > 0 ? (
						<>
							<Formik
								validationSchema={validationSchema}
								initialValues={{
									company: constraintData?.company,
									directAgents: constraintData?.directAgents,
									directAgentCount: constraintData?.directAgents?.length ?? 0,
									saleProfessionals: constraintData?.saleProfessionals,
									saleProfessionalCount: constraintData?.saleProfessionals?.length ?? 0,
									agentPartners: constraintData?.agentPartners,
									agentPartnerCount: constraintData?.agentPartners?.length ?? 0,
									brokerPartners: constraintData?.brokerPartners,
									brokerPartnerCount: constraintData?.brokerPartners?.length ?? 0,
									directAgentReason: '',
									directAgentInternalReason: '',
									saleStaffReason: '',
									saleStaffInternalReason: '',
									agentPartnerReason: '',
									agentPartnerInternalReason: '',
									agentPartnerDocument: null,
									brokerPartnerReason: '',
									brokerPartnerInternalReason: '',
									brokerPartnerDocument: null,
									agreement: false,
								}}
								onSubmit={(values) => {
									disableCompanyStatus({
										variables: {
											companyId: company?.id,
											directAgentReason: values?.directAgentReason,
											directAgentInternalReason: values?.directAgentInternalReason,
											saleStaffReason: values?.saleStaffReason,
											saleStaffInternalReason: values?.saleStaffInternalReason,
											agentPartnerReason: values?.agentPartnerReason,
											agentPartnerInternalReason: values?.agentPartnerInternalReason,
											agentPartnerDocument: values?.agentPartnerDocument,
											agentPartnerDocumentSize: values?.agentPartnerDocument?.size,
											brokerPartnerReason: values?.brokerPartnerReason,
											brokerPartnerInternalReason: values?.brokerPartnerInternalReason,
											brokerPartnerDocument: values?.brokerPartnerDocument,
											brokerPartnerDocumentSize: values?.brokerPartnerDocument?.size,
										},
									})
										.then((result) => {
											onClose();
											onDataChange();
											toast({
												title: t('Company updated successfully'),
												status: 'success',
												isClosable: true,
												position: 'top-right',
											});
										})
										.catch((error) => {
											toast({
												title: t(error?.message ? error?.message : 'something went wrong'),
												status: 'error',
												isClosable: true,
												position: 'bottom-right',
											});
										});
								}}
							>
								{({ errors, values, ...props }) => {
									return (
										<Modal
											size="xl"
											borderRadius={c.borderRadius}
											isOpen={company?.id ? true : false}
											onClose={onClose}
											closeOnEsc={loadingStatusContraint ? false : true}
											closeOnOverlayClick={loadingStatusContraint ? false : true}
										>
											<ModalOverlay />
											<ModalContent borderRadius={c.borderRadius}>
												<Form>
													<ModalHeader>{t('Deactivate company')}</ModalHeader>
													<ModalCloseButton isDisabled={loadingStatusContraint} />
													<ModalBody>
														<Text fontSize="md" fontWeight="normal" borderColor="gray.600">
															{t('Please reviewing the following constraints before proceeding to disable company')}
														</Text>
														<SimpleGrid width="100%" columns={1} spacingY={2} mt={2}>
															{values?.directAgentCount > 0 && (
																<SimpleGrid
																	columns={1}
																	spacing={2}
																	borderWidth={2}
																	borderStyle="dotted"
																	borderColor={
																		errors?.directAgentReason || errors?.directAgentInternalReason ? 'red.300' : 'gray.600'
																	}
																	mt={1}
																	mb={1}
																	p={4}
																	pt={1}
																>
																	<Box>
																		<Text fontSize="sm" fontWeight="normal" color="gray.600">
																			{t('Direct agent(s) will be dismissed from the company')}
																			<span style={{ fontWeight: 'bold', color: '#ef4444' }}>
																				&nbsp;({values?.directAgentCount})
																			</span>
																		</Text>
																	</Box>
																	<InputField {...props} size="sm" placeholder={t('Reason')} name="directAgentReason" />
																	<InputField
																		{...props}
																		size="sm"
																		placeholder={t('Internal reason')}
																		name="directAgentInternalReason"
																	/>
																</SimpleGrid>
															)}
															{values?.saleProfessionalCount > 0 && (
																<SimpleGrid
																	columns={1}
																	spacing={2}
																	borderWidth={2}
																	borderStyle="dotted"
																	borderColor={
																		errors?.saleStaffReason || errors?.saleStaffInternalReason ? 'red.300' : 'gray.600'
																	}
																	mt={1}
																	mb={1}
																	p={4}
																	pt={1}
																>
																	<Box>
																		<Text fontSize="sm" fontWeight="normal" color="gray.600">
																			{t('Sale staff(s) will be dismissed from the company')}
																			<span style={{ fontWeight: 'bold', color: '#ef4444' }}>
																				&nbsp;({values?.saleProfessionalCount})
																			</span>
																		</Text>
																	</Box>
																	<InputField {...props} size="sm" placeholder={t('Reason')} name="saleStaffReason" />
																	<InputField
																		{...props}
																		size="sm"
																		placeholder={t('Internal reason')}
																		name="saleStaffInternalReason"
																	/>
																</SimpleGrid>
															)}
															{values?.agentPartnerCount > 0 && (
																<SimpleGrid
																	columns={1}
																	spacing={2}
																	borderWidth={2}
																	borderStyle="dotted"
																	borderColor={
																		errors?.agentPartnerReason || errors?.agentPartnerInternalReason
																			? 'red.300'
																			: 'gray.600'
																	}
																	mt={1}
																	mb={1}
																	p={4}
																	pt={1}
																>
																	<Box>
																		{isInsuranceCompany(constraintData?.company?.type) && (
																			<Box>
																				<Text fontSize="sm" fontWeight="normal" color="gray.600">
																					{t('Partnering with intermediary company will be terminated')}
																					<span style={{ fontWeight: 'bold', color: '#ef4444' }}>
																						&nbsp;({values?.agentPartnerCount})
																					</span>
																				</Text>
																				<Box>
																					{values?.agentPartners?.map((agentPartner, i) => (
																						<Text
																							key={i}
																							color="red.500"
																							fontSize="sm"
																							fontWeight="600"
																						>{`${lang === 'kh' ? agentPartner?.intermediary?.nameKhmer : agentPartner?.intermediary?.name} (${t(agentPartner?.model)})`}</Text>
																					))}
																				</Box>
																			</Box>
																		)}
																		{isAgentCompany(constraintData?.company?.type) && (
																			<Box>
																				<Text fontSize="sm" fontWeight="normal" color="gray.600">
																					{t('Partnering with insurance company will be terminated')}
																					<span style={{ fontWeight: 'bold', color: '#ef4444' }}>
																						&nbsp;({values?.agentPartnerCount})
																					</span>
																				</Text>
																				<Box>
																					{values?.agentPartners?.map((agentPartner, i) => (
																						<Text
																							key={i}
																							color="red.500"
																							fontSize="sm"
																							fontWeight="600"
																						>{`${lang === 'kh' ? agentPartner?.company?.nameKhmer : agentPartner?.company?.name} (${t(agentPartner?.model)})`}</Text>
																					))}
																				</Box>
																			</Box>
																		)}
																	</Box>
																	<Text fontSize="sm" fontWeight="normal" color="gray.600">
																		{t('Document')}
																	</Text>
																	<FileFormField
																		{...props}
																		label={null}
																		name="agentPartnerDocument"
																		maxFileSize={c.maxFileSize / 20}
																	/>
																	<Text fontSize="sm" fontWeight="normal" color="gray.600">
																		{t('Professional Individual/Sale Staff')}
																	</Text>
																	<InputField {...props} size="sm" placeholder={t('Reason')} name="agentPartnerReason" />
																	<InputField
																		{...props}
																		size="sm"
																		placeholder={t('Internal reason')}
																		name="agentPartnerInternalReason"
																	/>
																</SimpleGrid>
															)}
															{values?.brokerPartnerCount > 0 && (
																<SimpleGrid
																	columns={1}
																	spacing={2}
																	borderWidth={2}
																	borderStyle="dotted"
																	borderColor={
																		errors?.brokerPartnerReason || errors?.brokerPartnerInternalReason
																			? 'red.300'
																			: 'gray.600'
																	}
																	mt={1}
																	mb={1}
																	p={4}
																	pt={1}
																>
																	<Box>
																		{isInsuranceCompany(constraintData?.company?.type) && (
																			<Box>
																				<Text fontSize="sm" fontWeight="normal" color="gray.600">
																					{t('Partnering with broker company will be terminated')}
																					<span style={{ fontWeight: 'bold', color: '#ef4444' }}>
																						&nbsp;({values?.brokerPartnerCount})
																					</span>
																				</Text>
																				<Box>
																					{values?.agentPartners?.map((agentPartner, i) => (
																						<Text
																							key={i}
																							color="red.500"
																							fontSize="sm"
																							fontWeight="600"
																						>{`${lang === 'kh' ? agentPartner?.intermediary?.nameKhmer : agentPartner?.intermediary?.name} (${t(agentPartner?.model)})`}</Text>
																					))}
																				</Box>
																			</Box>
																		)}
																		{isBrokerCompany(constraintData?.company?.type) && (
																			<Box>
																				<Text fontSize="sm" fontWeight="normal" color="gray.600">
																					{t('Partnering with insurance company will be terminated')}
																					<span style={{ fontWeight: 'bold', color: '#ef4444' }}>
																						&nbsp;({values?.brokerPartnerCount})
																					</span>
																				</Text>
																				<Box>
																					{values?.agentPartners?.map((agentPartner, i) => (
																						<Text
																							key={i}
																							color="red.500"
																							fontSize="sm"
																							fontWeight="600"
																						>{`${lang === 'kh' ? agentPartner?.company?.nameKhmer : agentPartner?.company?.name} (${t(agentPartner?.model)})`}</Text>
																					))}
																				</Box>
																			</Box>
																		)}
																	</Box>
																	<Text fontSize="sm" fontWeight="normal" color="gray.600">
																		{t('Document')}
																	</Text>
																	<FileFormField
																		{...props}
																		label={null}
																		name="brokerPartnerDocument"
																		maxFileSize={c.maxFileSize / 20}
																	/>
																	<Text fontSize="sm" fontWeight="normal" color="gray.600">
																		{t('Professional Individual')}
																	</Text>
																	<InputField {...props} size="sm" placeholder={t('Reason')} name="brokerPartnerReason" />
																	<InputField
																		{...props}
																		size="sm"
																		placeholder={t('Internal reason')}
																		name="brokerPartnerInternalReason"
																	/>
																</SimpleGrid>
															)}
															<Field name="agreement">
																{({ field, meta, form }) => {
																	return (
																		<Box mb="4">
																			<FormControl id="agreement">
																				<Checkbox
																					{...field}
																					isChecked={field?.value}
																					onChange={(e) => form.setFieldValue('agreement', e?.target?.checked)}
																				>
																					<Text color={meta.touched && meta.error ? 'red.400' : 'gray.600'}>
																						{t('Agree on dismission and proceeding to disable company')}
																					</Text>
																				</Checkbox>
																			</FormControl>
																		</Box>
																	);
																}}
															</Field>
														</SimpleGrid>
													</ModalBody>
													<ModalFooter>
														<Flex justifyContent="flex-end" alignItems="center">
															<HStack>
																<Button variant="solid" size="md" colorScheme="gray" onMouseDown={onClose}>
																	{t('Cancel')}
																</Button>
																<Button variant="solid" size="md" colorScheme="red" type="submit">
																	{t('Okay')}
																</Button>
															</HStack>
														</Flex>
													</ModalFooter>
												</Form>
											</ModalContent>
										</Modal>
									);
								}}
							</Formik>
						</>
					) : (
						<Modal
							borderRadius={c.borderRadius}
							isOpen={company?.id ? true : false}
							onClose={onClose}
							closeOnEsc={loadingStatus ? false : true}
							closeOnOverlayClick={loadingStatus ? false : true}
						>
							<ModalOverlay />
							<ModalContent borderRadius={c.borderRadius}>
								<ModalHeader>{t(company?.active ? 'Deactivate company' : 'Activate company')}</ModalHeader>
								<ModalCloseButton isDisabled={loadingStatus} />
								<ModalBody>
									<Text fontSize="md" fontWeight="normal" borderColor="gray.600">
										{t(company?.active ? 'Are you sure to deactivate this company?' : 'Are you sure to activate this company?')}
									</Text>
									<Flex justifyContent="flex-end" alignItems="center" mt={4} mb={2}>
										<HStack>
											<Button variant="solid" size="md" colorScheme="gray" onMouseDown={onClose}>
												{t('Cancel')}
											</Button>
											<Button
												variant="solid"
												size="md"
												colorScheme={company?.active ? 'red' : 'green'}
												onMouseDown={() =>
													updateCompanyStatus({
														variables: {
															companyId: company?.id,
															status: company?.active ? false : true,
														},
													})
														.then((result) => {
															onClose();
															onDataChange();
															toast({
																title: t('Company updated successfully'),
																status: 'success',
																isClosable: true,
																position: 'top-right',
															});
														})
														.catch((error) => {
															toast({
																title: t(error?.message ? error?.message : 'something went wrong'),
																status: 'error',
																isClosable: true,
																position: 'bottom-right',
															});
														})
												}
											>
												{t('Okay')}
											</Button>
										</HStack>
									</Flex>
								</ModalBody>
							</ModalContent>
						</Modal>
					)}
				</>
			)}
		</>
	);
}
